export const RSE_VIEW_ROLES = Object.freeze([1, 2, 6, 8, 10, 11, 13]);
export const RSE_EXPORT_ROLES = Object.freeze([1, 2, 6, 8, 10]);

export const RSE_INITIAL_FILTERS = Object.freeze({
  projectId: null,
  periodMode: "history",
  year: "",
  month: "",
  desde: "",
  hasta: "",
});

export const RSE_PERIOD_OPTIONS = Object.freeze([
  { value: "history", label: "Histórico completo" },
  { value: "year", label: "Año" },
  { value: "month", label: "Mes" },
  { value: "range", label: "Rango personalizado" },
]);

export const RSE_MONTH_OPTIONS = Object.freeze([
  { value: 1, label: "Enero" },
  { value: 2, label: "Febrero" },
  { value: 3, label: "Marzo" },
  { value: 4, label: "Abril" },
  { value: 5, label: "Mayo" },
  { value: 6, label: "Junio" },
  { value: 7, label: "Julio" },
  { value: 8, label: "Agosto" },
  { value: 9, label: "Septiembre" },
  { value: 10, label: "Octubre" },
  { value: 11, label: "Noviembre" },
  { value: 12, label: "Diciembre" },
]);

const toRole = (user) => Number(user?.rol);

export const getStoredRseUser = () => {
  try {
    return JSON.parse(localStorage.getItem("token")) || null;
  } catch (error) {
    return null;
  }
};

export const canAccessRse = (user) => RSE_VIEW_ROLES.includes(toRole(user));

export const canExportRse = (user) => RSE_EXPORT_ROLES.includes(toRole(user));

export const getRseYearOptions = (currentYear = new Date().getFullYear()) =>
  Array.from({ length: 16 }, (_, index) => currentYear - index);

export const changeRsePeriodMode = (
  filters,
  periodMode,
  currentYear = new Date().getFullYear()
) => {
  const next = { ...filters, periodMode };

  if (periodMode === "history") {
    return { ...next, year: "", month: "", desde: "", hasta: "" };
  }

  if (periodMode === "year") {
    return {
      ...next,
      year: filters.year || String(currentYear),
      month: "",
      desde: "",
      hasta: "",
    };
  }

  if (periodMode === "month") {
    return {
      ...next,
      year: filters.year || String(currentYear),
      month: filters.month || "",
      desde: "",
      hasta: "",
    };
  }

  if (periodMode === "range") {
    return { ...next, year: "", month: "", desde: "", hasta: "" };
  }

  return { ...RSE_INITIAL_FILTERS, projectId: filters.projectId };
};

export const validateRseFilters = (filters) => {
  if (filters.periodMode === "year" && !filters.year) {
    return { valid: false, message: "Selecciona un año." };
  }

  if (filters.periodMode === "month") {
    if (!filters.year) return { valid: false, message: "Selecciona un año." };
    if (!filters.month) return { valid: false, message: "Selecciona un mes." };

    const month = Number(filters.month);
    if (!Number.isInteger(month) || month < 1 || month > 12) {
      return { valid: false, message: "Selecciona un mes válido." };
    }
  }

  if (filters.periodMode === "range") {
    if (!filters.desde) return { valid: false, message: "Selecciona la fecha desde." };
    if (!filters.hasta) return { valid: false, message: "Selecciona la fecha hasta." };
    if (filters.desde > filters.hasta) {
      return {
        valid: false,
        message: "La fecha desde no puede ser posterior a la fecha hasta.",
      };
    }
  }

  return { valid: true, message: "" };
};

export const buildRseQueryParams = (filters) => {
  const params = {};

  if (filters.projectId !== null && filters.projectId !== "") {
    params.proyectoId = filters.projectId;
  }

  if (filters.periodMode === "year") {
    params.anio = Number(filters.year);
  } else if (filters.periodMode === "month") {
    params.anio = Number(filters.year);
    params.mes = Number(filters.month);
  } else if (filters.periodMode === "range") {
    params.desde = filters.desde;
    params.hasta = filters.hasta;
  }

  return params;
};

const getHeader = (headers, name) => {
  if (!headers) return "";
  if (typeof headers.get === "function") return headers.get(name) || "";

  const key = Object.keys(headers).find((header) => header.toLowerCase() === name.toLowerCase());
  return key ? headers[key] : "";
};

const decodeFilename = (value) => {
  const normalized = String(value || "").trim().replace(/^['"]|['"]$/g, "");

  try {
    return decodeURIComponent(normalized);
  } catch (error) {
    return normalized;
  }
};

const toSafeBasename = (value) => {
  const basename = String(value || "").replace(/\\/g, "/").split("/").pop();
  const withoutControlCharacters = Array.from(String(basename || ""))
    .map((character) => {
      const code = character.charCodeAt(0);
      return code < 32 || code === 127 ? "_" : character;
    })
    .join("");
  const safeName = withoutControlCharacters.replace(/[<>:"/\\|?*]/g, "_").trim();

  return safeName && safeName !== "." && safeName !== ".." ? safeName : "";
};

export const getDownloadFilename = (headers) => {
  const disposition = getHeader(headers, "content-disposition");
  const extendedMatch = disposition.match(/filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i);

  if (extendedMatch) {
    const extendedName = toSafeBasename(decodeFilename(extendedMatch[1]));
    if (extendedName) return extendedName;
  }

  const regularMatch = disposition.match(/filename\s*=\s*(?:"([^"]+)"|([^;]+))/i);
  if (regularMatch) {
    const regularName = toSafeBasename(decodeFilename(regularMatch[1] || regularMatch[2]));
    if (regularName) return regularName;
  }

  return "reporte-salud-emergencia.xlsx";
};

export const getRseErrorMessage = (error, action = "summary") => {
  if (error?.code === "ECONNABORTED") {
    return "La solicitud tardó demasiado. Intenta nuevamente o reduce el período.";
  }

  if (!error?.response) {
    return "No fue posible conectar con el servidor. Verifica tu conexión.";
  }

  const status = Number(error.response.status || 0);

  if (status === 400) return "Revisa los filtros seleccionados e intenta nuevamente.";
  if (status === 401) return "Tu sesión no es válida o ha expirado. Inicia sesión nuevamente.";
  if (status === 403) return "No tienes permisos para realizar esta acción.";
  if (status === 404 && action === "export") {
    return "No hay información disponible para generar el reporte.";
  }
  if (status === 404) return "No se encontraron datos para los filtros seleccionados.";

  return "No fue posible completar la solicitud. Intenta nuevamente.";
};

export const formatRseNumber = (value) =>
  new Intl.NumberFormat("es-VE", { maximumFractionDigits: 0 }).format(Number(value) || 0);

const rsePercentageFormatter = new Intl.NumberFormat("es-VE", {
  style: "percent",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

export const formatRsePercentage = (value) => {
  if (value === null || value === undefined || value === "") return "";

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? rsePercentageFormatter.format(numericValue) : "";
};

export const takeTopRseRows = (rows, valueKey = "atenciones", limit = 10) =>
  Array.isArray(rows)
    ? [...rows]
        .sort((left, right) => Number(right?.[valueKey] || 0) - Number(left?.[valueKey] || 0))
        .slice(0, limit)
    : [];
